import cn from 'classnames'
import {useEffect, useRef, useState} from 'react'

import {BTN_PRIMARY, CHAR_SPEED, GAME_RULES_URL, GAME_TERMS_URL, TAGLINE_TEXT} from '../constants'
import {MuteButton} from './MuteButton'

export const Intro = ({
  onStart,
  onFirstInteraction,
  muted,
  onToggleMute,
}: {
  onStart: () => void
  onFirstInteraction?: () => void
  muted: boolean
  onToggleMute: () => void
}) => {
  const hasTriggeredAudio = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const flashlightRef = useRef<HTMLDivElement>(null)
  const cursorRef = useRef<HTMLDivElement>(null)
  const logoRef = useRef<HTMLDivElement>(null)
  const logoRevealRef = useRef<HTMLImageElement>(null)

  // ── Entrance animation state ──
  const [showLogo, setShowLogo] = useState(false)
  const [typedChars, setTypedChars] = useState(0)
  const [showBody, setShowBody] = useState(false)
  const [showButton, setShowButton] = useState(false)
  const [showPrizesHeading, setShowPrizesHeading] = useState(false)
  const [showPrizeCards, setShowPrizeCards] = useState(false)
  const [exiting, setExiting] = useState(false)

  const handleStart = () => {
    setExiting(true)
    window.scrollTo({top: 0})
    setTimeout(() => onStart(), 900)
  }

  // Entrance animation sequence
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = []

    // Phase 1: Logo fades in after 100ms delay, 1600ms duration (handled via CSS transition)
    timers.push(setTimeout(() => setShowLogo(true), 100))

    // Phase 2: Tagline typewriter starts after logo fade
    const taglineStart = 100 + 1600 + 50

    for (let i = 0; i <= TAGLINE_TEXT.length; i++) {
      timers.push(setTimeout(() => setTypedChars(i), taglineStart + i * CHAR_SPEED))
    }

    // Phase 3: Body fades in after tagline finishes
    const bodyStart = taglineStart + TAGLINE_TEXT.length * CHAR_SPEED + 400
    timers.push(setTimeout(() => setShowBody(true), bodyStart))

    // Phase 4: Button fades in after body text finishes (1000ms fade duration + small gap)
    const buttonStart = bodyStart + 1000 + 200
    timers.push(setTimeout(() => setShowButton(true), buttonStart))

    // Phase 5: Prizes heading fades in after button
    timers.push(setTimeout(() => setShowPrizesHeading(true), buttonStart + 800))

    // Phase 6: Prize cards fade in after heading
    timers.push(setTimeout(() => setShowPrizeCards(true), buttonStart + 1400))

    return () => timers.forEach(clearTimeout)
  }, [])

  // Flashlight + shadow effect
  useEffect(() => {
    const container = containerRef.current

    if (!container) {
      return
    }

    function handleMouseMove(e: MouseEvent) {
      const rect = container!.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      // Move flashlight
      if (flashlightRef.current) {
        flashlightRef.current.style.background = `radial-gradient(circle 120px at ${x}px ${y}px, rgba(255,255,255,0.14) 0%, transparent 100%)`
      }

      // Move custom cursor
      if (cursorRef.current) {
        cursorRef.current.style.left = `${x}px`
        cursorRef.current.style.top = `${y}px`
      }

      // Logo shadow — directional drop shadow away from cursor, constant opacity
      if (logoRef.current) {
        const logoRect = logoRef.current.getBoundingClientRect()
        const logoCx = logoRect.left + logoRect.width / 2 - rect.left
        const logoCy = logoRect.top + logoRect.height / 2 - rect.top
        const dx = logoCx - x
        const dy = logoCy - y
        const dist = Math.sqrt(dx * dx + dy * dy) || 1
        const maxOffset = 8
        const sx = (dx / dist) * maxOffset
        const sy = (dy / dist) * maxOffset
        logoRef.current.style.filter = `drop-shadow(${sx}px ${sy}px 8px rgba(0,0,0,0.6))`
      }

      // Logo reveal — radial mask follows cursor over the bright logo layer
      if (logoRevealRef.current) {
        const logoRect = logoRevealRef.current.getBoundingClientRect()
        const lx = e.clientX - logoRect.left
        const ly = e.clientY - logoRect.top
        const mask = `radial-gradient(circle 120px at ${lx}px ${ly}px, black 0%, transparent 100%)`
        logoRevealRef.current.style.maskImage = mask
        logoRevealRef.current.style.webkitMaskImage = mask
      }
    }

    function handleClick() {
      if (!hasTriggeredAudio.current) {
        hasTriggeredAudio.current = true
        onFirstInteraction?.()
      }
    }

    container.addEventListener('mousemove', handleMouseMove)
    container.addEventListener('click', handleClick)

    return () => {
      container.removeEventListener('mousemove', handleMouseMove)
      container.removeEventListener('click', handleClick)
    }
  }, [onFirstInteraction])

  return (
    <div ref={containerRef} className="relative" style={{cursor: 'none'}}>
      {/* ── Above the fold ── */}
      <div className="relative flex min-h-screen flex-col items-center px-6 pb-8 text-center">
        {/* Black overlay for exit transition — scoped to this section */}
        <div
          className="pointer-events-none absolute inset-0 z-40 bg-moss-black transition-opacity duration-500 ease-in"
          style={{opacity: exiting ? 1 : 0}}
        />
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-35 mix-blend-overlay"
          style={{backgroundImage: 'url(/images/fraud-game/papertrails.png)'}}
        />

        {/* Flashlight overlay */}
        <div ref={flashlightRef} className="pointer-events-none absolute inset-0 z-10" />

        {/* Custom cursor dot */}
        <div
          ref={cursorRef}
          className="pointer-events-none absolute z-50 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-moss-white"
        />

        {/* Music toggle — top right */}
        <MuteButton
          muted={muted}
          onToggle={onToggleMute}
          className="absolute right-6 top-[100px] z-20 md:right-[30px] md:top-20"
        />

        {/* Content — centred */}
        <div className="relative z-10 flex flex-col items-center" style={{marginTop: '100px'}}>
          {/* Moss presents */}
          <div className="mb-8 flex items-center justify-center gap-2">
            <img src="/icons/moss-logo-v2.svg" alt="Moss" className="h-4 opacity-50 brightness-0 invert 2xl:h-5" />
            <span className="text-[10px] font-medium uppercase tracking-[.15em] text-moss-white/50 2xl:text-12">
              presents
            </span>
          </div>

          {/* Papertrails logo — fade in + flashlight effect */}
          <div
            ref={logoRef}
            className={cn('relative mb-4 w-full max-w-[560px] transition-[filter] duration-75 2xl:max-w-[722px]', {
              'opacity-0': !showLogo,
            })}
            style={showLogo ? {opacity: 1, transition: 'opacity 1600ms ease-in'} : {opacity: 0}}
          >
            {/* Base layer: dimmed */}

            <img src="/images/fraud-game/papertrails-logo.svg" alt="Papertrails" className="w-full opacity-60" />
            {/* Top layer: full brightness, masked by flashlight */}

            <img
              ref={logoRevealRef}
              src="/images/fraud-game/papertrails-logo.svg"
              alt=""
              className="pointer-events-none absolute inset-0 w-full"
              style={{
                maskImage: 'radial-gradient(circle 120px at -999px -999px, black 0%, transparent 100%)',
                WebkitMaskImage: 'radial-gradient(circle 120px at -999px -999px, black 0%, transparent 100%)',
              }}
            />
          </div>

          {/* Tagline — typewriter effect */}
          <p className="font-typewriter mb-8 h-5 text-14 text-moss-white/70">
            {typedChars > 0 && TAGLINE_TEXT.slice(0, typedChars)}
            {typedChars > 0 && (
              <span className="text-green-neon" style={{animation: 'blink 1s step-end infinite'}}>
                |
              </span>
            )}
          </p>

          {/* Description — fade in */}
          <p
            className={cn('mb-4 max-w-md text-14 leading-relaxed text-grey-300 transition-opacity duration-1000', {
              'opacity-0': !showBody,
              'opacity-100': showBody,
            })}
          >
            8 questions stand between you and the truth. Test your instincts, get your verdict, and see where you rank.
            Top investigators win prizes.
          </p>

          {/* CTA — fade in */}
          <button
            onClick={handleStart}
            className={cn(BTN_PRIMARY, 'mt-2 cursor-none transition-opacity duration-1000', {
              '!opacity-0': !showButton,
              'opacity-100': showButton,
            })}
            disabled={!showButton}
          >
            Start the investigation
          </button>
          <p
            className={cn('mt-3 text-center text-12 text-moss-grey-500 transition-opacity duration-1000 md:hidden', {
              '!opacity-0': !showButton,
              'opacity-100': showButton,
            })}
          >
            Best experienced on a laptop or desktop
          </p>
        </div>

        {/* Prizes — "What's at stake" (outside centered content, pushed to bottom) */}
        <div className="relative z-10 mt-auto flex flex-col items-center pb-8">
          <div style={showPrizesHeading ? {animation: 'fade-up 800ms ease-out both'} : {opacity: 0}}>
            <h3 className="text-center text-[15px] font-medium leading-normal text-moss-white">What&apos;s at stake</h3>
            <p className="mt-[3px] text-center text-14 leading-normal text-moss-grey-400">Play for a chance to win</p>
          </div>

          <div
            className="mt-6 flex items-center gap-8"
            style={showPrizeCards ? {animation: 'fade-up 800ms ease-out 200ms both'} : {opacity: 0}}
          >
            {/* Prize 1 — Apple AirPods */}
            <div className="group/prize relative">
              <img
                src="/images/fraud-game/thumbtack.png"
                alt=""
                className="absolute left-2 top-0 z-10 size-5 -scale-x-100"
              />
              <div
                className="relative flex w-[100px] rotate-[5deg] flex-col items-center justify-center gap-[8px] overflow-hidden rounded-[11px] border-[1.4px] border-[#3d3c3c] px-[12px] py-[16px]"
                style={{
                  background: 'linear-gradient(144deg, rgba(16,16,16,0.5) 2.35%, rgba(52,52,52,0.5) 96.08%)',
                  backdropFilter: 'blur(2.7px)',
                }}
              >
                <img
                  src="/images/fraud-game/prize-airpods.png"
                  alt="Apple AirPods"
                  className="h-[38px] w-[38px] shrink-0 rounded-[5.4px] object-cover"
                />
                <span className="text-center text-[14px] font-medium leading-tight text-moss-white">
                  Apple
                  <br />
                  AirPods
                </span>
                <div
                  className="pointer-events-none absolute inset-0 -translate-x-full skew-x-[-20deg] transition-transform duration-700 ease-out group-hover/prize:translate-x-full"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), rgba(255,255,255,0.06), transparent)',
                  }}
                />
              </div>
            </div>

            {/* Prize 2 — Treatwell Voucher */}
            <div className="group/prize relative">
              <img
                src="/images/fraud-game/thumbtack.png"
                alt=""
                className="absolute right-3 top-[-10px] z-10 size-5 rotate-[-10deg]"
              />
              <div
                className="relative flex w-[100px] rotate-[-3deg] flex-col items-center justify-center gap-[8px] overflow-hidden rounded-[11px] border-[1.4px] border-[#3d3c3c] px-[12px] py-[16px]"
                style={{
                  background: 'linear-gradient(144deg, rgba(16,16,16,0.5) 2.35%, rgba(52,52,52,0.5) 96.08%)',
                  backdropFilter: 'blur(2.7px)',
                }}
              >
                <img
                  src="/images/fraud-game/prize-treatwell.png"
                  alt="Treatwell Voucher"
                  className="h-[38px] w-[38px] shrink-0 rounded-[5.4px] object-cover"
                />
                <span className="text-center text-[14px] font-medium leading-tight text-moss-white">
                  Treatwell
                  <br />
                  Voucher
                </span>
                <div
                  className="pointer-events-none absolute inset-0 -translate-x-full skew-x-[-20deg] transition-transform duration-700 ease-out group-hover/prize:translate-x-full"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), rgba(255,255,255,0.06), transparent)',
                  }}
                />
              </div>
            </div>

            {/* Prize 3 — Finance Merch */}
            <div className="group/prize relative">
              <img
                src="/images/fraud-game/thumbtack.png"
                alt=""
                className="absolute right-[3px] top-[5px] z-10 size-5"
              />
              <div
                className="relative flex w-[100px] rotate-[4deg] flex-col items-center justify-center gap-[8px] overflow-hidden rounded-[11px] border-[1.4px] border-[#3d3c3c] px-[12px] py-[16px]"
                style={{
                  background: 'linear-gradient(144deg, rgba(16,16,16,0.5) 2.35%, rgba(52,52,52,0.5) 96.08%)',
                  backdropFilter: 'blur(2.7px)',
                }}
              >
                <img
                  src="/images/fraud-game/prize-moss.png"
                  alt="Finance Merch"
                  className="h-[38px] w-[38px] shrink-0 rounded-[5.4px] object-cover"
                />
                <span className="text-center text-[14px] font-medium leading-tight text-moss-white">
                  Finance
                  <br />
                  Merch
                </span>
                <div
                  className="pointer-events-none absolute inset-0 -translate-x-full skew-x-[-20deg] transition-transform duration-700 ease-out group-hover/prize:translate-x-full"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), rgba(255,255,255,0.06), transparent)',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Learn more */}
          <button
            onClick={() => document.getElementById('below-fold')?.scrollIntoView({behavior: 'smooth'})}
            className="mt-8 flex cursor-none flex-col items-center gap-1 text-12 font-medium uppercase tracking-[.15em] text-moss-grey-400 transition-colors hover:text-moss-white"
            style={showPrizeCards ? {animation: 'fade-up 800ms ease-out 400ms both'} : {opacity: 0}}
          >
            Learn more
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="animate-bounce">
              <path
                d="M4 6L8 10L12 6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════ */}
      {/* Below the fold                                        */}
      {/* ══════════════════════════════════════════════════════ */}
      <div id="below-fold" className="relative z-10 mx-auto w-full max-w-4xl px-6 py-20">
        {/* ── How It Works + FAQs side by side ── */}
        <div className="mb-16 grid gap-16 lg:grid-cols-2">
          {/* How It Works (left) */}
          <div>
            <h2 className="mb-8 text-[15px] font-medium uppercase tracking-[.15em] text-moss-white">How it works</h2>

            <div className="space-y-6">
              <div className="flex gap-4">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-[#3d3c3c] text-13 font-semibold text-green-neon">
                  1
                </span>
                <div>
                  <h3 className="mb-1 text-14 font-medium text-moss-white">Test Your Instincts</h3>
                  <p className="text-13 leading-relaxed text-moss-grey-400">
                    Answer 8 questions across two phases. Phase 1 tests your general fraud knowledge. Phase 2 puts you
                    in the hot seat with real-world scenarios.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-[#3d3c3c] text-13 font-semibold text-green-neon">
                  2
                </span>
                <div>
                  <h3 className="mb-1 text-14 font-medium text-moss-white">Get Your Verdict</h3>
                  <p className="text-13 leading-relaxed text-moss-grey-400">
                    See your score, find out how sharp your instincts really are, and learn from the finance insights
                    along the way.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-[#3d3c3c] text-13 font-semibold text-green-neon">
                  3
                </span>
                <div>
                  <h3 className="mb-1 text-14 font-medium text-moss-white">Climb the Leaderboard</h3>
                  <p className="text-13 leading-relaxed text-moss-grey-400">
                    Compare your score against other investigators. Top performers win prizes.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* FAQs (right) */}
          <div>
            <h2 className="mb-8 text-[15px] font-medium uppercase tracking-[.15em] text-moss-white">FAQs</h2>

            <div className="divide-y divide-[#3d3c3c] border-y border-[#3d3c3c]">
              <FaqItem question="When does the participation period end & when is the winner selection date?">
                The game is open from May 6 to June 6, 2026. Winners will be announced on Monday, June 8, 2026 and
                contacted directly via email.
              </FaqItem>
              <FaqItem question="How long does the game take & can I play more than once?">
                About 5 minutes. There are 8 questions with a timer on each. You can play up to two times and your best
                score counts.
              </FaqItem>
              <FaqItem question="What are the prizes?">
                Top investigators can win Apple AirPods &amp; Treatwell vouchers. Everyone scoring above 50 points
                enters the draw for exclusive finance merch.
              </FaqItem>
              <FaqItem question="How is my score calculated?">
                You earn points for correct answers. Faster responses earn bonus points.
              </FaqItem>
              <FaqItem question="Do I need to create an account?">
                No account needed to play. To appear on the leaderboard, you&apos;ll need to submit your email.
              </FaqItem>
            </div>
          </div>
        </div>

        {/* ── Divider ── */}
        <div className="mb-16 border-t border-[#3d3c3c]" />

        {/* ── Legal / Fine Print ── */}
        <div className="mb-8">
          <h2 className="mb-6 text-center text-[15px] font-medium uppercase tracking-[.15em] text-moss-white">
            Terms &amp; Conditions
          </h2>

          <div className="space-y-4 text-12 leading-relaxed text-moss-grey-400">
            <p>
              <span className="font-medium text-moss-grey-300">Eligibility.</span> Open to participants from the
              Netherlands, UK and Germany aged 18 and over.
            </p>
            <p>
              <span className="font-medium text-moss-grey-300">Prize Draw.</span> Winners will be selected from the top
              performers on the leaderboard at the end of the campaign period. Prizes are non-transferable and cannot be
              exchanged for cash. The promoter reserves the right to substitute prizes of equal or greater value.
            </p>
            <p>
              <span className="font-medium text-moss-grey-300">Data Privacy.</span> Your email address is only used for
              leaderboard participation and prize notification. We will not share your information with third parties.
            </p>
            <p>
              <span className="font-medium text-moss-grey-300">General.</span> The promoter is Moss GmbH. By
              participating, you agree to these terms. The promoter reserves the right to modify or cancel the game at
              any time.{' '}
              <a
                href={GAME_TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline transition-colors hover:text-moss-white"
              >
                Game T&amp;C
              </a>
              {' | '}
              <a
                href={GAME_RULES_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline transition-colors hover:text-moss-white"
              >
                Game Rules
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

const FaqItem = ({question, children}: {question: string; children: React.ReactNode}) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <button
      onClick={() => setIsOpen(!isOpen)}
      className={cn('grid w-full text-left transition-[grid-template-rows] duration-300 ease-in-out', {
        'grid-rows-[auto_0fr]': !isOpen,
        'grid-rows-[auto_1fr]': isOpen,
      })}
    >
      <div className="flex items-center justify-between py-4">
        <span className="text-14 font-medium text-moss-white">{question}</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          className={cn('ml-4 shrink-0 transition-transform duration-300', {'rotate-180': isOpen})}
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div className={cn('min-h-0 overflow-hidden transition-opacity duration-300', {'opacity-0': !isOpen})}>
        <p className="pb-4 text-13 leading-relaxed text-moss-grey-400">{children}</p>
      </div>
    </button>
  )
}
